import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { useToastContext } from '../../../../../../../contexts'
import { useMode } from '../../../../../../../hooks/useMode'
import { getHandleableError } from '../../../../../../../utils/errors/isXiaError'
import { useAllCompanyCodes } from '../../../../company-codes/data'
import {
  requestCreateSupplier,
  requestUpdateSupplier,
  useAllCustomFieldDefinitions,
  useAllMasterDataSuppliers,
  useSupplierById,
} from '../../../data'
import { FormValues } from '../common'
import { EditSupplierForm } from './EditSupplier.form'

export const EditSupplierData: React.FC = () => {
  const { t } = useTranslation()
  const { showToast } = useToastContext()
  const { supplierId } = useParams()
  const navigate = useNavigate()
  const { data: supplier } = useSupplierById(supplierId, true, true)
  const { data: allCustomFieldDefinitions } = useAllCustomFieldDefinitions()
  const { data: allCompanyCodes } = useAllCompanyCodes()
  const { data: allMasterDataSuppliers } = useAllMasterDataSuppliers()

  const mode = useMode()

  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)

  const dtoFromFormData = (formData: FormValues): any => {
    // Convert MasterDataSupplier to SupplierMasterDataRequest format
    return {
      Common: {
        DisplayId: formData.displayId,
        Company: formData.company,
        SalesTaxID: formData.salesTaxID,
        TaxNumber: formData.taxNumber,
      },
      Address: {
        Street: formData.street,
        PostalCode: formData.postalCode,
        City: formData.city,
        Country: formData.country,
      },
      CustomFields:
        formData.customFields?.map(customField => ({
          CustomFieldDefinitionId: customField.CustomFieldDefinition.Id,
          Value: customField.Value.toString(),
        })) || [],
      CompanyCodes: formData.companyCodes || [],
    }
  }

  const handleSubmit = async (formData: FormValues, resetForm: any) => {
    try {
      setIsRequestRunning(true)

      const dto = dtoFromFormData(formData)

      if (mode === 'add') await requestCreateSupplier(dto)
      if (mode === 'edit') await requestUpdateSupplier(supplierId!, dto)

      resetForm()

      const summary =
        mode === 'add'
          ? t(
              'masterDataManagement.suppliers.table.dialog.add.messages.success.summary',
            )
          : t(
              'masterDataManagement.suppliers.table.dialog.save.messages.success.summary',
            )
      const detail =
        mode === 'add'
          ? t(
              'masterDataManagement.suppliers.table.dialog.add.messages.success.detail',
              {
                company: formData?.company,
              },
            )
          : t(
              'masterDataManagement.suppliers.table.dialog.save.messages.success.detail',
              {
                company: formData?.company,
              },
            )

      showToast({
        severity: 'success',
        summary: summary,
        detail: detail,
      })

      handleClose()
    } catch (error) {
      const handleableError = getHandleableError(error)
      const errorMessage = handleableError.message
      const summary =
        mode === 'add'
          ? t(
              'masterDataManagement.suppliers.table.dialog.add.messages.failure.summary',
            )
          : t(
              'masterDataManagement.suppliers.table.dialog.save.messages.failure.summary',
            )

      showToast({
        severity: 'error',
        summary: summary,
        detail: errorMessage,
        sticky: true,
      })
    } finally {
      setIsRequestRunning(false)
    }
  }

  const handleClose = () => {
    navigate('/master-data-management/suppliers')
  }

  const customFieldsOptions = allCustomFieldDefinitions?.filter(
    def => def.Type === 'Supplier',
  )

  return (
    <EditSupplierForm
      handleSubmit={handleSubmit}
      isRequestRunning={isRequestRunning}
      handleClose={handleClose}
      supplier={supplier}
      customFieldsOptions={customFieldsOptions}
      companyCodesOptions={allCompanyCodes}
      allMasterDataSuppliers={allMasterDataSuppliers}
    />
  )
}
